<template>
  <NuxtRouteAnnouncer/>
  <v-app>
    <v-app-bar>
      <v-app-bar-title style="min-width: 165px">
        <nuxt-link to="/">
          <v-img
            src="/wiresock-logo.png"
            width="165"
            height="23"
            title="Wiresock logo"
            alt="Wiresock logo"
          />
        </nuxt-link>
      </v-app-bar-title>

      <template #append>
        <nav class="ws-nav-menu mr-8">

          <div class="hidden-sm-and-down ml-8">
            <v-toolbar-items>
              <template
                v-for="(menuItem,index) in menu"
                :key="menuItem.path"
              >
                <template v-if="menuItem.expandable">
                  <v-menu
                    open-on-click
                    open-on-focus
                    open-delay="0"
                  >
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="{...props, ...bindLinks(menuItem)}"
                        class="ws-nav-menu-item"
                        @click.prevent.capture
                        :active="isParentOfCurrent(menuItem)"
                      >
                        {{ menuItem.name }}
                        <v-icon
                          icon="mdi-menu-down"
                          end
                        ></v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item
                        v-for="(subMenuItem, subIndex) in menuItem.children"
                        :key="subMenuItem.path"
                        v-bind="bindLinks(subMenuItem)"
                      >
                        {{ subMenuItem.name }}
                        <v-chip
                          v-if="subMenuItem.badge"
                          size="x-small"
                          color="primary"
                          variant="tonal"
                          class="ml-2"
                        >
                          {{ subMenuItem.badge }}
                        </v-chip>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
                <template v-else>
                  <v-btn
                    class="ws-nav-menu-item"
                    v-bind="bindLinks(menuItem)"
                  >{{ menuItem.name }}
                  </v-btn>
                </template>
              </template>
            </v-toolbar-items>
          </div>

          <v-toolbar-items>
            <v-btn
              to="/account/"
              icon="mdi-account"
              v-tooltip="'Account'"
            >
            </v-btn>
          </v-toolbar-items>

          <v-app-bar-nav-icon
            class="hidden-md-and-up"
            @click.stop="isMenuDrawerShown=!isMenuDrawerShown"
          ></v-app-bar-nav-icon>
          <!--          <menu-tree />-->
        </nav>
      </template>
    </v-app-bar>
    <v-navigation-drawer
      v-if="isMenuDrawerShown"
      class="hidden-md-and-up"
      :model-value="isMenuDrawerShown"
      location="right"
      @update:model-value="isMenuDrawerShown=$event"
    >
      <menu-tree :menu="menu"/>
    </v-navigation-drawer>

    <slot/>

    <v-divider class="mb-4 mt-16"/>
    <v-footer>
      <div
        style="width: 1024px"
        class="mx-auto page-footer"
      >
        <div>
          <support-links
            display-mode="list"
            header-class="text-h6"
            links-size="small"
            is-header-hidden
            links-color="gray"
            youtube
          />
        </div>

        <div class="mt-8 d-flex ga-1 flex-wrap">
          <nuxt-link to="/about/privacy-policy" class="text-grey px-2 ml-n2">Privacy Policy</nuxt-link>
          <nuxt-link to="/about/terms-of-service" class="text-grey px-2">Terms of Service</nuxt-link>
          <nuxt-link to="/about/refund-policy" class="text-grey px-2">Refund Policy</nuxt-link>
        </div>
        <div class="mt-4">
          Tools:
          <nuxt-link to="/tools/check-ip" class="text-primary">Check IP</nuxt-link>
        </div>
        <div class="font-weight-light mt-8">
          Copyright © Wiresock Foundation, 2021-{{ currentYear }}.
        </div>
        <div class="font-weight-light mt-2 mb-16">
          WireGuard is a registered trademark of Jason A. Donenfeld.
          WireSock project is not sponsored or endorsed by Jason A. Donenfeld.
        </div>

      </div>
    </v-footer>

    <cookie-consent-banner />

    <ClientOnly>
      <assistant-widget />
    </ClientOnly>

  </v-app>

</template>

<script
  setup
  lang="ts"
>
import { bindLinks, getMenuTree } from '~~/shared/services/menu/MenuService';
import type { MenuItem } from '~~/shared/services/menu/MenuItem';
import { useRoute } from '#app';

// AI support assistant (012-support-chat-assistant): loaded lazily, client-only, so it adds nothing to the page bundle.
const AssistantWidget = defineAsyncComponent(() => import('~/components/support-assistant/AssistantWidget.vue'));

useHead({
  titleTemplate: (titleChunk) => {
    return titleChunk ? `${ titleChunk } – WireSock Secure Connect` : 'WireSock Secure Connect - Advanced VPN Client';
  },
});

const route = useRoute();
const menu = getMenuTree();
const isMenuDrawerShown = ref(false);
const currentYear = new Date().getFullYear();

const isParentOfCurrent = (menuItem: MenuItem): boolean | undefined => {
  if (!menuItem.parentPrefix) {
    return undefined;
  }
  const prefixes: string[] = typeof menuItem.parentPrefix === 'string' ? [ menuItem.parentPrefix ] : menuItem.parentPrefix;
  return prefixes?.some((i) => route.path.startsWith(i));
};
</script>

<style lang="css">
.ws-nav-menu {
  display: flex;
}

.ws-nav-menu-item {
  color: black;
  padding: 4px 16px;
}

.router-link-active {
  color: gray;
  text-decoration: none;
  background: #f0f0f0
}

.router-link-active--exact {
  color: gray;
}

.page-footer {
  padding: 16px 0 24px;
  font-size: 14px;
  color: gray;
}
</style>

